// Phase I3.A — Pure adapter functions that translate database row shapes
// (bet_builder_slips + super_acca_disciplinary + super_acca_territorial +
// super_acca_player_performance + correct_score_insights + clv_consensus)
// into the typed view-models the panels render.
//
// Doctrine: never fabricate. When a source row is missing or its sample
// size flag is not satisfied, the adapter returns null and the panel
// renders the operator-facing empty-state copy.

import type { CategoryId, LayerId, TierId } from "./categories";
import { marketLabel, selectionLabel } from "../exchange/market-format.ts";

// ─── Slip view-model ──────────────────────────────────────────────

export interface SuperAccaLeg {
  id: number;
  market: string;
  selection: string;
  odds: number;
  model_prob: number | null;
  market_prob: number | null;
  edge_pct: number | null;
  category: CategoryId | null;
  layer: LayerId | null;
  anchor: boolean;
  flagged: boolean;
  legConfidence: number | null;
  legConfidenceLabel: string | null;
  reasoning_short: string | null;
  reasoning_long: string | null;
  correlated_with: number[];
  primary_market: string | null;
}

export interface SharpMoneyView {
  signal: "CONFIRMS" | "CHALLENGES" | "NEUTRAL" | null;
  open: number | null;
  now: number | null;
  drift_pp: number | null;
}

export interface AgentDebateEntry {
  agent: string;
  role: string;
  stance: "CONFIRM" | "CHALLENGE" | "CONSENSUS";
  note: string;
}

export interface AdversarialEntry {
  leg_id: number;
  challenge: string;
  response: string;
  cleared: boolean;
}

export interface SuperAccaSlip {
  id: string;
  pick_date: string;
  fixture_id: string;
  fixture_name: string | null;
  league: string | null;
  kickoff_at: string | null;
  legs: SuperAccaLeg[];
  combined_odds: number;
  confidence: number | null;
  rationale: string | null;
  status: "published" | "abstained" | "superseded";
  outcome: "won" | "lost" | "pending" | "void" | null;
  tier: TierId;
  contradiction_passed: boolean;
  adversarial_cleared: boolean;
  correlation_applied: boolean;
  sharp_money: SharpMoneyView;
  agent_debate: AgentDebateEntry[];
  adversarial: AdversarialEntry[];
  build_your_own_legs: BYOMarketCandidate[];
}

// ─── DNA panels ───────────────────────────────────────────────────

export interface DisciplinaryIntelView {
  fixture_id: string;
  referee_name: string | null;
  appointments: number | null;
  cards_avg: number | null;
  fouls_avg: number | null;
  pen_rate: number | null;
  red_rate: number | null;
  context_label: string | null;
  context_multiplier: number | null;
  expected_total_cards: number | null;
  expected_total_fouls: number | null;
  first_card_min: number | null;
  player_card_candidates: PlayerCardCandidate[];
  edges: DisciplinaryEdge[];
  /** True when the underlying agent has enough samples to publish numbers. */
  sample_valid: boolean;
}

export interface PlayerCardCandidate {
  name: string;
  team: string;
  card_rate: number | null;
  combined_prob: number | null;
}

export interface DisciplinaryEdge {
  market: string;
  selection: string;
  edge_pp: number;
}

export interface TerritorialIntelView {
  fixture_id: string;
  expected_total_corners: number | null;
  expected_first_half_corners: number | null;
  asian_corners_recommendation: string | null;
  early_corners_10min_prob: number | null;
  tactical_matchup: string | null;
  corner_edge_detected: boolean;
  edges: { market: string; selection: string; edge_pp: number }[];
  home_corners_avg: number | null;
  away_corners_avg: number | null;
  h2h_avg: number | null;
  matchup_uplift: number | null;
  sample_valid: boolean;
}

export interface PlayerDNAEntry {
  name: string;
  team: string;
  shots90: number | null;
  sot90: number | null;
  ag: number | null;
  streak: string | null;
  risk: string | null;
  risk_color: "ok" | "warn";
}

export interface PlayerPerformanceView {
  fixture_id: string;
  goalscorer_candidates: PlayerDNAEntry[];
  assist_candidates: PlayerDNAEntry[];
  shots_candidates: PlayerDNAEntry[];
  card_candidates: PlayerDNAEntry[];
  edges: { market: string; selection: string; edge_pp: number }[];
  observed_players: number;
  sample_valid: boolean;
  /** Combined "ALL" view: deduplicated by name+team. */
  all_players: PlayerDNAEntry[];
}

// ─── Scoreline matrix ─────────────────────────────────────────────

export interface ScorelineCell {
  score: string;
  model: number;
  implied: number;
  edge: number;
}

export interface DerivedMarketsView {
  over_under: { line: number; true_prob: number; market_prob: number; edge: number } | null;
  btts: { true_prob: number; market_prob: number; edge: number } | null;
  ah: { line: string; true_prob: number; market_prob: number; edge: number } | null;
  draw: { true_prob: number; market_prob: number; edge: number } | null;
  htft: { recommended: string; model_prob: number } | null;
}

// ─── BYO market library ───────────────────────────────────────────

export interface BYOMarketCandidate {
  id: string;
  category: CategoryId;
  market: string;
  odds: number | null;
  state: "edge" | "compatible" | "contradicts" | "no_edge";
  model: number | null;
  implied: number | null;
  edge: number | null;
  ev: string | null;
  sharp: "CONFIRMS" | "CHALLENGES" | "NEUTRAL" | null;
  note: string | null;
  conflicts_with: number | null;
  reason: string | null;
}

// ──────────────────────────────────────────────────────────────────
// Coercers
// ──────────────────────────────────────────────────────────────────

function asN(v: unknown): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") return Number(v);
  return Number.NaN;
}
function asNn(v: unknown): number | null {
  const n = asN(v);
  return Number.isFinite(n) ? n : null;
}
function asStr(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}
function asBool(v: unknown): boolean {
  return v === true;
}
function asArr(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}
function asObj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

const VALID_CATEGORIES: CategoryId[] = ["OUTCOME", "DISCIPLINE", "TERRITORY", "PLAYER"];
const VALID_LAYERS: LayerId[] = ["FOUNDATION", "AMPLIFIER", "MAX", "BYO"];

function asCategory(v: unknown): CategoryId | null {
  if (typeof v !== "string") return null;
  const u = v.toUpperCase();
  return (VALID_CATEGORIES as string[]).includes(u) ? (u as CategoryId) : null;
}
function asLayer(v: unknown): LayerId | null {
  if (typeof v !== "string") return null;
  const u = v.toUpperCase();
  return (VALID_LAYERS as string[]).includes(u) ? (u as LayerId) : null;
}

// ──────────────────────────────────────────────────────────────────
// Slip → SuperAccaSlip
// ──────────────────────────────────────────────────────────────────

export function adaptSlip(row: Record<string, unknown> | null): SuperAccaSlip | null {
  if (!row || typeof row.id !== "string") return null;

  const status = row.status;
  const tierRaw = typeof row.slip_tier === "string" ? row.slip_tier.toLowerCase() : "standard";
  const tier: TierId =
    tierRaw === "conservative" || tierRaw === "standard" || tierRaw === "maximum"
      ? (tierRaw as TierId)
      : "standard";

  return {
    id: row.id,
    pick_date: asStr(row.pick_date) ?? "",
    fixture_id: asStr(row.fixture_id) ?? "",
    fixture_name: asStr(row.fixture_name),
    league: asStr(row.league),
    kickoff_at: asStr(row.kickoff_at),
    legs: adaptLegs(row.legs, (() => {
      const parts = asStr(row.fixture_name)?.split(/ vs | v | – |—/);
      return { home_team: parts?.[0]?.trim() ?? null, away_team: parts?.[1]?.trim() ?? null };
    })()),
    combined_odds: asN(row.combined_odds),
    confidence: asNn(row.confidence),
    rationale: asStr(row.rationale),
    status:
      status === "published" || status === "abstained" || status === "superseded"
        ? status
        : "published",
    outcome:
      row.outcome === "won" ||
      row.outcome === "lost" ||
      row.outcome === "pending" ||
      row.outcome === "void"
        ? row.outcome
        : null,
    tier,
    contradiction_passed: asBool(row.contradiction_passed),
    adversarial_cleared: asBool(row.adversarial_cleared),
    correlation_applied: asBool(row.correlation_applied),
    sharp_money: adaptSharpMoney(row.sharp_money_signal, row.sharp_money_meta),
    agent_debate: adaptAgentDebate(row.agent_debate_log),
    adversarial: adaptAdversarial(row.legs),
    build_your_own_legs: adaptBYO(row.build_your_own_legs),
  };
}

export function adaptLegs(
  raw: unknown,
  ctx?: { home_team?: string | null; away_team?: string | null },
): SuperAccaLeg[] {
  return asArr(raw)
    .map((leg, idx): SuperAccaLeg | null => {
      const r = asObj(leg);
      if (!r) return null;
      const market = asStr(r.market);
      const selection = asStr(r.selection);
      if (!market || !selection) return null;
      const correlated_with = asArr(r.correlated_with)
        .map((n) => asNn(n))
        .filter((n): n is number => n !== null);
      return {
        id: typeof r.id === "number" ? r.id : idx + 1,
        market: marketLabel(market),
        selection: selectionLabel(market, selection, ctx),
        odds: asN(r.odds),
        model_prob: asNn(r.model_prob),
        market_prob: asNn(r.market_prob),
        edge_pct: asNn(r.edge_pct),
        category: asCategory(r.category),
        layer: asLayer(r.layer),
        anchor: asBool(r.anchor),
        flagged: r.flagged !== undefined && r.flagged !== false && r.flagged !== null,
        legConfidence: asNn(r.legConfidence ?? r.leg_confidence),
        legConfidenceLabel: asStr(r.legConfidenceLabel ?? r.leg_confidence_label),
        reasoning_short: asStr(r.reasoning_short ?? r.reasoning),
        reasoning_long: asStr(r.reasoning_long),
        correlated_with,
        primary_market: asStr(r.primary_market),
      };
    })
    .filter((x): x is SuperAccaLeg => x !== null);
}

export function adaptSharpMoney(
  signal: unknown,
  meta: unknown,
): SharpMoneyView {
  const m = asObj(meta);
  const sigU = typeof signal === "string" ? signal.toUpperCase() : null;
  const valid: SharpMoneyView["signal"] =
    sigU === "CONFIRMS" || sigU === "CHALLENGES" || sigU === "NEUTRAL" ? sigU : null;
  return {
    signal: valid,
    open: asNn(m?.open),
    now: asNn(m?.now),
    drift_pp: asNn(m?.drift_pp ?? m?.drift_pct),
  };
}

export function adaptAgentDebate(raw: unknown): AgentDebateEntry[] {
  return asArr(raw)
    .map((d): AgentDebateEntry | null => {
      const r = asObj(d);
      if (!r) return null;
      const agent = asStr(r.agent);
      const note = asStr(r.note);
      const role = asStr(r.role) ?? "";
      const stanceRaw = typeof r.stance === "string" ? r.stance.toUpperCase() : null;
      const stance: AgentDebateEntry["stance"] =
        stanceRaw === "CONFIRM" || stanceRaw === "CHALLENGE" || stanceRaw === "CONSENSUS"
          ? stanceRaw
          : "CONSENSUS";
      if (!agent || !note) return null;
      return { agent, role, stance, note };
    })
    .filter((x): x is AgentDebateEntry => x !== null);
}

export function adaptAdversarial(rawLegs: unknown): AdversarialEntry[] {
  return asArr(rawLegs)
    .map((leg, i): AdversarialEntry | null => {
      const r = asObj(leg);
      if (!r) return null;
      const challenge = asStr(r.adversarial_challenge);
      const response = asStr(r.adversarial_response);
      if (!challenge || !response) return null;
      const id = typeof r.id === "number" ? r.id : i + 1;
      const flagged =
        r.flagged !== undefined && r.flagged !== false && r.flagged !== null;
      return { leg_id: id, challenge, response, cleared: !flagged };
    })
    .filter((x): x is AdversarialEntry => x !== null);
}

export function adaptBYO(raw: unknown): BYOMarketCandidate[] {
  return asArr(raw)
    .map((m): BYOMarketCandidate | null => {
      const r = asObj(m);
      if (!r) return null;
      const id = asStr(r.id);
      const market = asStr(r.market);
      if (!id || !market) return null;
      const cat = asCategory(r.category);
      if (!cat) return null;
      const stateRaw = typeof r.state === "string" ? r.state : "compatible";
      const state: BYOMarketCandidate["state"] =
        stateRaw === "edge" ||
        stateRaw === "compatible" ||
        stateRaw === "contradicts" ||
        stateRaw === "no_edge"
          ? stateRaw
          : "compatible";
      const sharpRaw = typeof r.sharp === "string" ? r.sharp.toUpperCase() : null;
      const sharp: BYOMarketCandidate["sharp"] =
        sharpRaw === "CONFIRMS" || sharpRaw === "CHALLENGES" || sharpRaw === "NEUTRAL"
          ? sharpRaw
          : null;
      return {
        id,
        category: cat,
        market,
        odds: asNn(r.odds),
        state,
        model: asNn(r.model),
        implied: asNn(r.implied),
        edge: asNn(r.edge),
        ev: asStr(r.ev),
        sharp,
        note: asStr(r.note),
        conflicts_with: asNn(r.conflicts_with),
        reason: asStr(r.reason),
      };
    })
    .filter((x): x is BYOMarketCandidate => x !== null);
}

// ──────────────────────────────────────────────────────────────────
// Disciplinary
// ──────────────────────────────────────────────────────────────────

export function adaptDisciplinary(
  row: Record<string, unknown> | null,
): DisciplinaryIntelView | null {
  if (!row) return null;
  const fixture_id = asStr(row.fixture_id);
  if (!fixture_id) return null;

  const cards_avg = asNn(row.referee_cards_avg);
  const appointments = asNn(row.appointments) ?? null;
  const sample_valid = cards_avg !== null && cards_avg > 0;

  const candidates = asArr(row.player_card_candidates)
    .map((c): PlayerCardCandidate | null => {
      const r = asObj(c);
      if (!r) return null;
      const name = asStr(r.name);
      const team = asStr(r.team);
      if (!name || !team) return null;
      return {
        name,
        team,
        card_rate: asNn(r.card_rate),
        combined_prob: asNn(r.combined_prob),
      };
    })
    .filter((x): x is PlayerCardCandidate => x !== null);

  const edges = asArr(row.disc_edges)
    .map((e): DisciplinaryEdge | null => {
      const r = asObj(e);
      if (!r) return null;
      const market = asStr(r.market);
      const selection = asStr(r.selection);
      const edge_pp = asNn(r.edge_pp);
      if (!market || !selection || edge_pp === null) return null;
      return { market, selection, edge_pp };
    })
    .filter((x): x is DisciplinaryEdge => x !== null);

  return {
    fixture_id,
    referee_name: asStr(row.referee_name),
    appointments,
    cards_avg,
    fouls_avg: asNn(row.referee_fouls_avg),
    pen_rate: asNn(row.pen_rate),
    red_rate: asNn(row.red_rate),
    context_label: asStr(row.context_label),
    context_multiplier: asNn(row.context_multiplier),
    expected_total_cards: asNn(row.expected_total_cards),
    expected_total_fouls: asNn(row.expected_total_fouls),
    first_card_min: asNn(row.first_card_timing_est),
    player_card_candidates: candidates,
    edges,
    sample_valid,
  };
}

// ──────────────────────────────────────────────────────────────────
// Territorial
// ──────────────────────────────────────────────────────────────────

export function adaptTerritorial(
  row: Record<string, unknown> | null,
): TerritorialIntelView | null {
  if (!row) return null;
  const fixture_id = asStr(row.fixture_id);
  if (!fixture_id) return null;
  const expected_total = asNn(row.expected_total_corners);
  const sample_valid = expected_total !== null && expected_total > 0;

  const edges = asArr(row.territory_edges)
    .map((e) => {
      const r = asObj(e);
      if (!r) return null;
      const market = asStr(r.market);
      const selection = asStr(r.selection);
      const edge_pp = asNn(r.edge_pp);
      if (!market || !selection || edge_pp === null) return null;
      return { market, selection, edge_pp };
    })
    .filter((x): x is { market: string; selection: string; edge_pp: number } => x !== null);

  return {
    fixture_id,
    expected_total_corners: expected_total,
    expected_first_half_corners: asNn(row.expected_first_half_corners),
    asian_corners_recommendation: asStr(row.asian_corners_recommendation),
    early_corners_10min_prob: asNn(row.early_corners_10min_prob),
    tactical_matchup: asStr(row.tactical_matchup),
    corner_edge_detected: asBool(row.corner_edge_detected),
    edges,
    home_corners_avg: asNn(row.home_corners_avg),
    away_corners_avg: asNn(row.away_corners_avg),
    h2h_avg: asNn(row.h2h_avg),
    matchup_uplift: asNn(row.matchup_uplift),
    sample_valid,
  };
}

// ──────────────────────────────────────────────────────────────────
// Player Performance
// ──────────────────────────────────────────────────────────────────

function adaptPlayerEntry(c: unknown): PlayerDNAEntry | null {
  const r = asObj(c);
  if (!r) return null;
  const name = asStr(r.name);
  const team = asStr(r.team);
  if (!name || !team) return null;
  const riskColorRaw = asStr(r.risk_color);
  return {
    name,
    team,
    shots90: asNn(r.shots90),
    sot90: asNn(r.sot90),
    ag: asNn(r.ag),
    streak: asStr(r.streak),
    risk: asStr(r.risk),
    risk_color: riskColorRaw === "warn" ? "warn" : "ok",
  };
}

export function adaptPlayerPerformance(
  row: Record<string, unknown> | null,
): PlayerPerformanceView | null {
  if (!row) return null;
  const fixture_id = asStr(row.fixture_id);
  if (!fixture_id) return null;

  const goalscorer = asArr(row.goalscorer_candidates)
    .map(adaptPlayerEntry)
    .filter((x): x is PlayerDNAEntry => x !== null);
  const assist = asArr(row.assist_candidates)
    .map(adaptPlayerEntry)
    .filter((x): x is PlayerDNAEntry => x !== null);
  const shots = asArr(row.shots_model)
    .map(adaptPlayerEntry)
    .filter((x): x is PlayerDNAEntry => x !== null);
  const cards = asArr(row.card_candidates)
    .map(adaptPlayerEntry)
    .filter((x): x is PlayerDNAEntry => x !== null);

  const edges = asArr(row.player_edges)
    .map((e) => {
      const r = asObj(e);
      if (!r) return null;
      const market = asStr(r.market);
      const selection = asStr(r.selection);
      const edge_pp = asNn(r.edge_pp);
      if (!market || !selection || edge_pp === null) return null;
      return { market, selection, edge_pp };
    })
    .filter((x): x is { market: string; selection: string; edge_pp: number } => x !== null);

  // dedupe by name+team for "all" view
  const seen = new Set<string>();
  const all_players: PlayerDNAEntry[] = [];
  for (const p of [...goalscorer, ...shots, ...assist, ...cards]) {
    const key = `${p.name}|${p.team}`;
    if (seen.has(key)) continue;
    seen.add(key);
    all_players.push(p);
  }

  const observed_players = all_players.length;
  const sample_valid = observed_players >= 1; // panel itself shows "M players observed"

  return {
    fixture_id,
    goalscorer_candidates: goalscorer,
    assist_candidates: assist,
    shots_candidates: shots,
    card_candidates: cards,
    edges,
    observed_players,
    sample_valid,
    all_players,
  };
}

// ──────────────────────────────────────────────────────────────────
// Scoreline matrix
// ──────────────────────────────────────────────────────────────────

export function adaptScorelineMatrix(
  row: Record<string, unknown> | null,
): ScorelineCell[] {
  if (!row) return [];
  // Prefer the rich `scoreline_grid` array if present; otherwise just the
  // single "best" scoreline as a one-cell matrix.
  const grid = asArr(row.scoreline_grid);
  if (grid.length > 0) {
    const cells = grid
      .map((g): ScorelineCell | null => {
        const r = asObj(g);
        if (!r) return null;
        const score = asStr(r.scoreline);
        const model = asNn(r.model_prob);
        const implied = asNn(r.implied_prob);
        if (!score || model === null) return null;
        const impliedV = implied ?? 0;
        const edge = Number((model - impliedV).toFixed(2));
        return {
          score,
          model: Number((model * (model > 1 ? 1 : 100)).toFixed(1)),
          implied: Number((impliedV * (impliedV > 1 ? 1 : 100)).toFixed(1)),
          edge: Number((edge * (Math.abs(edge) > 1 ? 1 : 100)).toFixed(1)),
        };
      })
      .filter((x): x is ScorelineCell => x !== null);
    cells.sort((a, b) => b.model - a.model);
    return cells.slice(0, 12);
  }
  const score = asStr(row.scoreline);
  const model = asNn(row.model_prob);
  const implied = asNn(row.implied_prob);
  if (!score || model === null) return [];
  const m = model > 1 ? model : model * 100;
  const i = implied !== null ? (implied > 1 ? implied : implied * 100) : 0;
  return [
    {
      score,
      model: Number(m.toFixed(1)),
      implied: Number(i.toFixed(1)),
      edge: Number((m - i).toFixed(1)),
    },
  ];
}

// Compute O/U, BTTS, AH and Draw/HT-FT views from the full grid + the slip's
// own derived markets when available. Pure: takes the matrix + the slip
// legs and surfaces the 5 derived tiles.
export function deriveMarkets(
  matrix: ScorelineCell[],
  slip: SuperAccaSlip | null,
): DerivedMarketsView {
  if (matrix.length === 0) {
    return { over_under: null, btts: null, ah: null, draw: null, htft: null };
  }
  // Sum probabilities across the grid for each derived market
  let over = 0,
    btts = 0,
    home = 0,
    draw = 0,
    away = 0;
  for (const c of matrix) {
    const [hRaw, aRaw] = c.score.split(/[-:]/);
    const h = Number(hRaw),
      a = Number(aRaw);
    if (!Number.isFinite(h) || !Number.isFinite(a)) continue;
    if (h + a >= 3) over += c.model;
    if (h >= 1 && a >= 1) btts += c.model;
    if (h > a) home += c.model;
    else if (h === a) draw += c.model;
    else away += c.model;
  }

  // Match slip leg market_prob for "market" estimates if we have the leg.
  function findLeg(...names: string[]): SuperAccaLeg | null {
    if (!slip) return null;
    return (
      slip.legs.find((l) =>
        names.some((n) => l.market.toLowerCase().includes(n.toLowerCase())),
      ) ?? null
    );
  }

  const ouLeg = findLeg("Total Goals", "Over/Under", "Over 2.5");
  const bttsLeg = findLeg("Both Teams to Score", "BTTS");
  const ahLeg = findLeg("Asian Handicap");

  const m = (n: number) => Number(n.toFixed(1));
  const ou_market = ouLeg?.market_prob ?? null;
  const btts_market = bttsLeg?.market_prob ?? null;
  const ah_market = ahLeg?.market_prob ?? null;

  return {
    over_under: {
      line: 2.5,
      true_prob: m(over),
      market_prob: ou_market ?? 0,
      edge: ou_market !== null ? m(over - ou_market) : 0,
    },
    btts: {
      true_prob: m(btts),
      market_prob: btts_market ?? 0,
      edge: btts_market !== null ? m(btts - btts_market) : 0,
    },
    ah: {
      line: ahLeg?.selection ?? "-0.5",
      true_prob: m(home),
      market_prob: ah_market ?? 0,
      edge: ah_market !== null ? m(home - ah_market) : 0,
    },
    draw: {
      true_prob: m(draw),
      market_prob: 0,
      edge: 0,
    },
    htft: {
      recommended: home > away ? "HOME/HOME" : away > home ? "AWAY/AWAY" : "DRAW/—",
      model_prob: m(Math.max(home, draw, away) * 0.4),
    },
  };
}
